/**
 * React hook for Google OAuth login and signup flows.
 * Handles OAuth initiation with automatic timeout protection,
 * error handling, and loading state management.
 *
 * @module hooks/use-google-oauth
 */

import { useState, useRef, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { logger } from '@/lib/logger';
import { logError } from '@/lib/utils';
import { api } from '@/lib/api';
import { classifyOAuthError } from '@/lib/utils/error-utils';

/**
 * Safety timeout for OAuth requests in milliseconds.
 *
 * OAuth requests typically complete in <10 seconds. This 35 second timeout
 * provides a buffer for slow networks while preventing the UI from being
 * stuck indefinitely on "Connecting...".
 *
 * If the timeout fires, we reset the loading state and show an error toast,
 * allowing the user to retry.
 */
const OAUTH_TIMEOUT_MS = 35000;

/**
 * Hook for initiating Google OAuth flows (login or signup).
 *
 * Provides automatic timeout protection (35s), loading state management,
 * and user-friendly error messages for common failure scenarios.
 *
 * @param type - 'login' or 'signup' flow type
 * @returns Object with initiate function and loading state
 *
 * @example
 * ```tsx
 * function LoginPage() {
 *   const { initiate, isLoading } = useGoogleOAuth('login');
 *
 *   return (
 *     <button onClick={initiate} disabled={isLoading}>
 *       {isLoading ? 'Connecting...' : 'Sign in with Google'}
 *     </button>
 *   );
 * }
 * ```
 *
 * @example
 * ```tsx
 * // Signup flow with referral code support
 * // Referral code is automatically extracted from URL (?ref=ABC123)
 * function SignupPage() {
 *   const { initiate, isLoading } = useGoogleOAuth('signup');
 *   return <button onClick={initiate}>Create Account</button>;
 * }
 * ```
 *
 * @see {@link ../../lib/api/handlers/auth/google-login.ts} - Login API handler
 * @see {@link ../../lib/api/handlers/auth/google-signup.ts} - Signup API handler
 */
export function useGoogleOAuth(type: 'login' | 'signup') {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const initiate = async () => {
    setIsLoading(true);

    // Safety timeout: if the request takes too long, reset loading state
    // This prevents the UI from being stuck on "Connecting..." forever
    timeoutRef.current = setTimeout(() => {
      logError(`Google OAuth ${type} request timed out after ${OAUTH_TIMEOUT_MS}ms`, {
        type,
        timeoutMs: OAUTH_TIMEOUT_MS,
      });
      setIsLoading(false);
      toast({
        title: `${type === 'login' ? 'Login' : 'Signup'} Timeout`,
        description: 'The request took too long. Please check your connection and try again.',
        variant: 'destructive',
      });
    }, OAUTH_TIMEOUT_MS);

    try {
      const referralCode =
        typeof window !== 'undefined' && type === 'signup'
          ? new URLSearchParams(window.location.search).get('ref')
          : null;

      logger.info(`Initiating Google OAuth ${type}`, { type, hasReferralCode: !!referralCode });

      const response =
        type === 'login'
          ? await api.initiateGoogleOAuthLogin()
          : await api.initiateGoogleOAuthSignup(referralCode);

      // Clear timeout since we got a response
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      if (!response?.authorization_url) {
        throw new Error('Invalid response: missing authorization_url');
      }

      logger.info(`Google OAuth ${type} initiated successfully, redirecting...`, {
        type,
        urlLength: response.authorization_url.length,
      });

      // Redirect to Google OAuth
      // Note: We don't reset isLoading here because the page will navigate away
      window.location.href = response.authorization_url;
    } catch (error) {
      // Clear timeout on error
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      const classified = classifyOAuthError(error);
      logError(
        `Failed to initiate Google OAuth ${type}`,
        {
          errorType: classified.type,
          type,
        },
        error
      );

      setIsLoading(false);

      toast({
        title: `${type === 'login' ? 'Login' : 'Signup'} Failed`,
        description: classified.message,
        variant: 'destructive',
      });
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return { initiate, isLoading };
}
